import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../components/common/PriceDisplay";
import {
    BOOLEAN_FIELD_TYPE,
    DATE_RANGE_FIELD_TYPE,
    NUMERIC_RANGE_FIELD_TYPE,
    SELECT_FIELD_TYPE,
    TEXT_FIELD_TYPE,
    AUTOCOMPLETE_FIELD_TYPE,
} from "../../helpers/constants";
import DateService from "../../services/dateService";
import { useOfferCancellationReasons, useOfferStatuses } from "../../services/offersService";
import { useInvestments, useProductTypes } from "../../services/productsService";
import { formatDecimalNumber, useLocalizedProperty } from "../../utils";
import { OfferManageButton } from "./OfferManageButton";
import { OfferStatusText } from "./OfferStatusText";

export const OFFERS_TABLE_NAME = "offers_table";

export const useOfferTableFieldsConfig = (refreshOffers) => {
    const { t } = useTranslation();

    const getLocalizedProperty = useLocalizedProperty();

    const { data: investments } = useInvestments({
        select: (data) =>
            data.results.map((investment) => ({
                value: investment.id,
                label: investment.investment_name_sales,
            })),
    });

    const { data: productTypesOptions } = useProductTypes(undefined, {
        select: (data) =>
            data.map((type) => ({
                id: type.id,
                label: getLocalizedProperty(type, "name"),
            })),
    });

    const { data: statuses } = useOfferStatuses({
        select: (data) =>
            data.results.map((status) => ({
                value: status.status_name_eng,
                label: getLocalizedProperty(status, "status_name"),
            })),
    });

    const { data: cancellationReasons } = useOfferCancellationReasons({
        select: (data) =>
            data.results.map((reason) => ({
                value: reason.id,
                label: getLocalizedProperty(reason, "reason_name"),
            })),
    });

    return [
        {
            name: "offerNumber",
            getValueCallback: (rowData) => rowData.offer_nr,
            label: t("table.offers.offerNumber"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: true,
        },
        {
            name: "customerNames",
            getValueCallback: (rowData) =>
                rowData.customers.map((c) => c.customer.name || "").join("\n"),
            label: t("table.offers.customerNames"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
        },
        {
            name: "customerLastNames",
            getValueCallback: (rowData) =>
                rowData.customers.map((c) => c.customer.last_name || "").join("\n"),
            label: t("table.offers.customerLastNames"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
        },
        {
            name: "customerCompanyNames",
            getValueCallback: (rowData) =>
                rowData.customers.map((c) => c.customer.company_name || "").join("\n"),
            label: t("table.offers.customerCompanyNames"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
        },
        {
            name: "investment",
            getValueCallback: (rowData) => rowData.investment_name,
            label: t("table.offers.investmentName"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => ({
                optionKey: "value",
                optionLabel: "label",
                options: investments,
                multiple: false,
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: investments,
            },
            sortable: false,
        },
        {
            name: "dateCreated",
            getValueCallback: (rowData) =>
                DateService.convertDatetimeFromBackendToFormatYYYYMMDD(rowData.date_created),
            label: t("table.offers.dateCreated"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
        },
        {
            name: "status",
            getValueCallback: (rowData) => <OfferStatusText status={rowData.status} />,
            label: t("table.offers.status"),
            filterType: SELECT_FIELD_TYPE,
            sortable: false,
            getFilterFieldConfigCallback: (_) => ({
                options: statuses,
                optionKey: "value",
                optionLabel: "label",
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: statuses,
            },
        },
        {
            name: "statusReason",
            getValueCallback: (rowData) =>
                rowData.status_reason
                    ? getLocalizedProperty(rowData.status_reason, "reason_name")
                    : "-",
            label: t("table.offers.statusReason"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => ({
                optionKey: "value",
                optionLabel: "label",
                options: cancellationReasons,
                multiple: false,
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: cancellationReasons,
            },
            sortable: false,
        },
        {
            name: "statusNote",
            getValueCallback: (rowData) => rowData.status_note,
            label: t("table.offers.statusNote"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
        },
        {
            name: "productNumbers",
            getValueCallback: (rowData) =>
                rowData.products.map((p) => p.product.product_number || "-").join("\n"),
            label: t("table.offers.productNumbers"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
        },
        {
            name: "productTypeId",
            other_export_field_name: "productType",
            getValueCallback: (rowData) =>
                rowData.products
                    .map((op) => op.product.type)
                    .map((type) => (type ? getLocalizedProperty(type, "name") : "-"))
                    .join("\n "),
            label: t("table.offers.productTypes"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
            minWidth: 120,
            width: 120,
            fieldConfig: {
                optionLabelKey: "label",
                options: productTypesOptions || [],
                multiple: true,
                isObjectOption: true,
                disableClearable: false
            },
            sortable: false,
        },
        {
            name: "listedPrices",
            getValueCallback: (rowData) =>
                rowData.products.map((p) => formatCurrency(p.listed_price)).join("\n"),
            label: t("table.offers.listedPrices"),
            sortable: false,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
        },
        {
            name: "listedPricesPerSqm",
            getValueCallback: (rowData) =>
                rowData.products.map((p) => formatCurrency(p.listed_price_per_sqm)).join("\n"),
            label: t("table.offers.listedPricesPerSqm"),
            sortable: false,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
        },
        {
            name: "discountPercentages",
            getValueCallback: (rowData) =>
                rowData.products.map((p) => `${formatDecimalNumber(p.discount_percent)}%`).join("\n"),
            label: t("table.offers.discountPercentages"),
            sortable: false,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
        },
        {
            name: "discountAmounts",
            getValueCallback: (rowData) =>
                rowData.products.map((p) => formatCurrency(p.discount_pln)).join("\n"),
            label: t("table.offers.discountAmounts"),
            sortable: false,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
        },
        {
            name: "discountAmountsPerSqm",
            getValueCallback: (rowData) =>
                rowData.products.map((p) => formatCurrency(p.discount_pln_per_sqm)).join("\n"),
            label: t("table.offers.discountAmountsPerSqm"),
            sortable: false,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
        },
        {
            name: "pricesAfterDiscount",
            getValueCallback: (rowData) =>
                rowData.products.map((p) => formatCurrency(p.price_after_discount)).join("\n"),
            label: t("table.offers.pricesAfterDiscount"),
            sortable: false,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
        },
        {
            name: "isTechnical",
            getValueCallback: (rowData) => (rowData.is_technical ? t("yes") : t("no")),
            label: t("table.offers.isTechnical"),
            filterType: BOOLEAN_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: true,
        },
        {
            name: "manage",
            getValueCallback: (rowData) => (
                <OfferManageButton offer={rowData} onRefresh={refreshOffers} />
            ),
            label: t("table.offers.manage"),
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
            excludedFromExport: true,
            allowChangeVisibility: false,
        },
    ];
};
